import { useEffect, useMemo, useRef } from "react";
import { useApp } from "../state/store";
import { buildLayout } from "../domain/layout";
import { NAVBAR_HEIGHT } from "../domain/insets";
import { drawCanvas } from "../canvas/draw";
import { useResizeObserver } from "../hooks/useResizeObserver";
import { usePanZoom } from "../hooks/usePanZoom";
import { DropZone } from "./DropZone";
import { StatusBar } from "./StatusBar";
import { useT } from "../i18n/store";

export const PreviewCanvas = () => {
  const t = useT();
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const source   = useApp((s) => s.source);
  const paper    = useApp((s) => s.paper);
  const margins  = useApp((s) => s.margins);
  const sign     = useApp((s) => s.sign);
  const tile     = useApp((s) => s.tile);
  const cut      = useApp((s) => s.cut);
  const view     = useApp((s) => s.view);
  const zoom     = useApp((s) => s.zoom);
  const pan      = useApp((s) => s.pan);
  const setHostSize = useApp((s) => s.setHostSize);

  const { width: hostW, height: hostH } = useResizeObserver(hostRef);

  // Mirror current host dimensions into store so Navbar zoom buttons can use them.
  useEffect(() => {
    setHostSize({ w: hostW, h: hostH });
  }, [hostW, hostH, setHostSize]);

  const layout = useMemo(
    () => buildLayout(paper, margins, sign, tile),
    [paper, margins, sign, tile],
  );

  const { onMouseDown } = usePanZoom({ hostRef });

  useEffect(() => {
    if (!canvasRef.current || hostW === 0 || hostH === 0) return;
    drawCanvas(
      canvasRef.current,
      source
        ? { hostW, hostH, topInset: NAVBAR_HEIGHT, layout, margins, sign, source, cut, view, zoom, pan }
        : null,
      hostW, hostH,
    );
  }, [hostW, hostH, layout, margins, sign, source, cut, view, zoom, pan]);

  return (
    <main className="canvas-area">
      <div
        ref={hostRef}
        className={`canvas-host${source ? " has-content" : ""}`}
        onMouseDown={onMouseDown}
      >
        <canvas ref={canvasRef} />
        {!source && (
          <div className="canvas-empty">
            <div className="canvas-empty-card">
              <DropZone />
              <div className="canvas-empty-tip">{t("tips.body")}</div>
            </div>
          </div>
        )}
      </div>
      <StatusBar layout={layout} />
    </main>
  );
};
